import type { IGatsbyImageData } from 'gatsby-plugin-image';
import { isEmpty } from 'lodash-es';

import Buttons from '@klv/molecules/buttons';
import KlaviyoForm from '@klv/atoms/klaviyo-form';
import Quote from '@klv/molecules/quote';
import MixedMedia from '@klv/molecules/mixed-media';
import Image from '@klv/atoms/image';
import VideoModalTrigger from '@klv/molecules/video-modal-trigger';
import { Wrapper } from '@klv/utils/wrapper';
import { getBackgroundType } from '@klv/utils/wrapper/functions';
import { generateClassName, hasImage, styleBadge } from '@klv/shared/utils/functions';
import * as styles from './styles/twoColumn.module.scss';
import type { TColorVariant } from '@klv/shared/styles/types';
import type { IMixedMedia } from '@klv/molecules/mixed-media/types';
import { LIGHT_BACKGROUNDS } from '@klv/shared/utils/variables';
import type { ITwoColumn, TMediaAlignment } from './types';

const hasMixedMedia = (mixedMedia?: IMixedMedia): boolean => {
  if (!isEmpty(mixedMedia)) {
    if (mixedMedia.mediaType === 'image') {
      return hasImage(mixedMedia.image);
    }
    if (mixedMedia.mediaType === 'rive') {
      return !!mixedMedia.rive?.riveFile;
    }
    return hasImage(mixedMedia?.video?.poster) && !!mixedMedia?.video?.videoUrl;
  }

  return false;
};

const TwoColumn = ({
  backgroundColor = 'light',
  body,
  buttons,
  customPadding = { customPaddingBottom: 'md', customPaddingTop: 'md' },
  eyebrow = '',
  headline = '',
  fadeIn = false,
  mediaAlignment,
  subhead = '',
  mixedMedia,
  CustomContent,
  MediaCustomContent,
  useVideoModal = false,
  videoModalContent = {
    videoHeadline: ' ',
    videoUrl: '',
    videoDuration: '',
    image: {
      altText: '',
    },
  },
}: ITwoColumn) => {
  const isQuote = !!(body && body?.length > 0 && body[0]?.bodyType?.includes('Quote')) || body && body?.length > 0 && body[0]?.quote;
  const background =  LIGHT_BACKGROUNDS.concat('light').includes(backgroundColor) ? 'poppy' : 'lemon';
  const backgroundType = getBackgroundType(backgroundColor);

  return (
    <Wrapper customPadding={customPadding} backgroundColor={backgroundColor} fadeIn={fadeIn ? .25 : 0}>
      <div>
        <div className={`grid ${styles.grid}`}>
          <div className={generateClassName([styles.gridText, styles[mediaAlignment as TMediaAlignment]])}>
            {eyebrow && <div className={`eyebrow ${styles.eyebrow}`}>{eyebrow}</div>}
            {headline && <h2 className={`h3 semibold ${styles.headline}`}>{headline}</h2>}
            {subhead && <div className={`p2 ${styles.subtext}`} dangerouslySetInnerHTML={{ __html: subhead }} />}
          </div>
          <div className={generateClassName([styles.gridTextLower, styles[mediaAlignment as TMediaAlignment]])}>
            {body && body?.length > 0 && (
              <div className={`p2 ${styles.body} ${styles[background]}`}>
                {(body[0]?.bodyType?.includes('RichText') || (body && body?.length > 0 && body[0]?.richText)) && (
                  <div className={styles.bodyText} dangerouslySetInnerHTML={{ __html: styleBadge(body[0].richText) }} />
                )}
                {(body[0]?.bodyType?.includes('KlaviyoForm') ||  (body && body?.length > 0 && body[0]?.formId)) && (
                  <KlaviyoForm formId={body[0].formId} />
                )}
                {isQuote && (
                  <Quote
                    backgroundColor={backgroundType as TColorVariant}
                    quote={body[0].quote}
                    attribution={body[0].attribution}
                  />
                )}
              </div>
            )}
            {CustomContent || (
              <Buttons
                buttons={buttons}
                variants={['ghost', 'tertiary']}
                buttonColor={backgroundType === 'light' ? 'dark' : 'light'}
              />
            )}
          </div>
          {MediaCustomContent || (
            <div className={generateClassName([styles.gridMedia, styles[mediaAlignment as TMediaAlignment]])}>
              {mixedMedia && hasMixedMedia(mixedMedia) && !useVideoModal && (
                <MixedMedia data-chromatic="ignore" mixedMedia={mixedMedia} />
              )}
              {useVideoModal && videoModalContent?.image?.gatsbyImage && videoModalContent?.videoUrl && (
                <VideoModalTrigger 
                  videoDuration={videoModalContent.videoDuration} 
                  modalHeadline={videoModalContent.videoHeadline} 
                  videoPlayButtonColor={videoModalContent.videoPlayButtonColor}
                  videoOverlayTitle={videoModalContent.videoOverlayTitle}
                  videoUrl={videoModalContent.videoUrl}
                >
                  <Image data-chromatic="ignore" altText={videoModalContent.image.altText} imageData={videoModalContent.image.gatsbyImage as IGatsbyImageData} />
                </VideoModalTrigger>
              )}
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default TwoColumn;
